import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error.response?.data?.detail) {
        // Handle validation errors
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          const errorMsg = detail.map(err => err.msg).join(', ');
          toast.error(`Erreur de validation: ${errorMsg}`);
        } else {
          toast.error(detail);
        }
      } else {
        toast.error('Une erreur est survenue. Veuillez réessayer plus tard.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-hero-title">Contactez-Nous</h1>
          <p className="page-hero-description">
            Une question, un projet ? N'hésitez pas à nous contacter
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info-section">
              <h2 className="contact-info-title">Informations de Contact</h2>
              <p className="contact-info-description">
                Nous sommes à votre écoute pour répondre à toutes vos demandes concernant 
                l'entretien et l'aménagement de vos espaces verts.
              </p>

              <div className="contact-info-list">
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="contact-info-label">Téléphone</h3>
                    <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="contact-info-value">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="contact-info-label">Email</h3>
                    <a href={`mailto:${companyInfo.email}`} className="contact-info-value">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="contact-info-label">Zone d'Intervention</h3>
                    <p className="contact-info-value">{companyInfo.address}</p>
                  </div>
                </div>
              </div>

              <div className="contact-hours">
                <h3 className="contact-hours-title">Horaires d'Ouverture</h3>
                <div className="contact-hours-list">
                  <div className="contact-hours-item">
                    <span>Lundi - Vendredi</span>
                    <span>8h00 - 18h00</span>
                  </div>
                  <div className="contact-hours-item">
                    <span>Samedi</span>
                    <span>9h00 - 17h00</span>
                  </div>
                  <div className="contact-hours-item">
                    <span>Dimanche</span>
                    <span>Fermé</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-section">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Nom Complet *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Votre nom"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Téléphone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject" className="form-label">Sujet *</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Ex: Demande de devis pour tonte de pelouse"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="form-textarea"
                    placeholder="Décrivez votre projet ou votre demande..."
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer le Message'}
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
